import { useId } from "react";

// Map each occasion to a decorative motif
const HEARTS = ["Love", "Valentine's Day", "Wedding", "Engagement", "Happy Anniversary", "Missing You", "I'm Sorry", "Mother's Day", "Father's Day", "Thinking of You"];
const STARS = ["Birthday", "Congratulations", "Graduation", "New Year", "Good Luck", "New Job", "Retirement", "Hanukkah"];
const CONFETTI = ["Welcome", "Friendship", "Encouragement", "Just Because", "Thank You", "Party Time"];

function motifFor(category) {
  if (HEARTS.includes(category)) return "hearts";
  if (STARS.includes(category)) return "stars";
  if (CONFETTI.includes(category)) return "confetti";
  return "petals";
}

function MotifShapes({ type }) {
  // Vibrant, semi-transparent palette so artwork pops on any gradient
  const Y = "rgba(255,224,102,0.92)";   // sunny yellow
  const M = "rgba(168,230,207,0.92)";   // mint
  const C = "rgba(255,140,148,0.95)";   // coral
  const S = "rgba(138,198,255,0.92)";   // sky blue
  const P = "rgba(255,181,216,0.95)";   // pink
  const V = "rgba(200,182,255,0.92)";   // lavender
  const W = "rgba(255,255,255,0.95)";   // white
  if (type === "hearts") {
    const heart = "M14 25 C14 18 4 17 4 10 C4 5 11 4 14 9 C17 4 24 5 24 10 C24 17 14 18 14 25 Z";
    return (
      <>
        <path d={heart} fill={C} transform="translate(2,4) scale(0.95)" />
        <path d={heart} fill={W} transform="translate(46,30) scale(0.7)" />
        <path d={heart} fill={Y} transform="translate(64,2) scale(0.55)" />
        <path d={heart} fill={P} transform="translate(30,52) scale(0.65)" />
        <path d={heart} fill={V} transform="translate(2,46) scale(0.5)" />
      </>
    );
  }
  if (type === "stars") {
    const star = "M14 1 L17 10 L27 10 L19 16 L22 26 L14 20 L6 26 L9 16 L1 10 L11 10 Z";
    return (
      <>
        <path d={star} fill={Y} transform="translate(4,6) scale(0.85)" />
        <path d={star} fill={W} transform="translate(50,34) scale(0.6)" />
        <path d={star} fill={S} transform="translate(62,4) scale(0.5)" />
        <path d={star} fill={M} transform="translate(28,50) scale(0.55)" />
        <path d={star} fill={P} transform="translate(2,44) scale(0.45)" />
      </>
    );
  }
  if (type === "confetti") {
    return (
      <>
        <rect x="6" y="8" width="11" height="5" rx="2" fill={C} transform="rotate(20 11 10)" />
        <rect x="50" y="14" width="10" height="5" rx="2" fill={S} transform="rotate(-25 54 16)" />
        <rect x="34" y="40" width="11" height="5" rx="2" fill={Y} transform="rotate(40 39 42)" />
        <circle cx="64" cy="48" r="4" fill={M} />
        <circle cx="20" cy="54" r="3.5" fill={P} />
        <rect x="58" y="2" width="9" height="4" rx="2" fill={V} transform="rotate(-15 64 4)" />
        <circle cx="40" cy="12" r="3" fill={W} />
      </>
    );
  }
  // petals / soft dots
  return (
    <>
      <ellipse cx="14" cy="14" rx="9" ry="5" fill={P} transform="rotate(35 14 14)" />
      <ellipse cx="56" cy="40" rx="8" ry="4.5" fill={M} transform="rotate(-30 56 40)" />
      <circle cx="60" cy="10" r="4.5" fill={Y} />
      <circle cx="28" cy="52" r="4" fill={V} />
      <ellipse cx="34" cy="22" rx="6" ry="3.5" fill={W} transform="rotate(15 34 22)" />
    </>
  );
}

/**
 * Decorative artwork layer for gradient templates — renders the gradient,
 * an occasion-matched motif pattern, a depth highlight, and the title.
 */
export function TemplateArt({ gradient, category, title, titleClass = "text-3xl" }) {
  const pid = useId().replace(/:/g, "");
  const type = motifFor(category);
  const tile = type === "confetti" || type === "petals" ? 72 : 78;
  return (
    <div className="absolute inset-0 w-full h-full overflow-hidden" style={{ background: gradient }}>
      <svg className="absolute inset-0 w-full h-full" aria-hidden>
        <defs>
          <pattern id={`p-${pid}`} width={tile} height={tile} patternUnits="userSpaceOnUse" patternTransform="rotate(6)">
            <MotifShapes type={type} />
          </pattern>
          <radialGradient id={`g-${pid}`} cx="30%" cy="20%" r="80%">
            <stop offset="0%" stopColor="rgba(255,255,255,0.45)" />
            <stop offset="55%" stopColor="rgba(255,255,255,0)" />
          </radialGradient>
        </defs>
        <rect width="100%" height="100%" fill={`url(#p-${pid})`} opacity="0.55" />
        <rect width="100%" height="100%" fill={`url(#g-${pid})`} />
      </svg>
      <div className="absolute inset-0 grid place-items-center px-4">
        <span className={`font-display font-bold text-white text-center leading-tight drop-shadow-[2px_3px_0_rgba(0,0,0,0.28)] ${titleClass}`}>
          {title}
        </span>
      </div>
    </div>
  );
}
